import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, ConflictError } from '../api/client';
import type { EquipmentType, Frequency, LocationType } from '../api/types';
import { useApp } from '../state/AppContext';
import { ConflictBanner, EmptyRow, ErrorBanner, SortableHeader, useTableSort } from '../components/ui';

/**
 * What can be counted, and what each kind of location is asked to count and
 * how often. A thirteenth container type is a row added here, never a change
 * to the app.
 */
export function Equipment() {
  const { canEditSetup } = useApp();
  const [notice, setNotice] = useState<string | null>(null);

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Supplies</div>
          <div className="page-subtitle">What is counted, and what each type of location is asked to count</div>
        </div>
      </div>
      {!canEditSetup && (
        <div className="meta" style={{ marginBottom: 12 }}>You can see the setup. Changing it needs Setup at all regions.</div>
      )}
      {notice && <div className="banner banner--info"><div>{notice}</div></div>}

      <EquipmentList canEdit={canEditSetup} onSaved={setNotice} />
      <CountLists canEdit={canEditSetup} onSaved={setNotice} />
    </div>
  );
}

function EquipmentList({ canEdit, onSaved }: { canEdit: boolean; onSaved: (message: string) => void }) {
  const { reference } = useApp();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<EquipmentType | null>(null);
  // Active locations whose type has this supply on its count list.
  const countingIt = (e: EquipmentType) => reference.locations.filter((l) =>
    l.isActive && reference.types.some((t) => t.locationTypeId === l.locationTypeId && t.equipmentTypeIds.includes(e.equipmentTypeId))).length;
  const { rows, sort, onSort } = useTableSort(reference.equipment, {
    name: (e) => e.name,
    locations: countingIt,
    status: (e) => (e.isActive ? 'Active' : 'Inactive'),
  });
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: (e: EquipmentType) => {
      const body = { name: e.name, isActive: e.isActive, rowVersionNo: e.rowVersionNo };
      return e.equipmentTypeId > 0 ? api.put(`/api/setup/equipment/${e.equipmentTypeId}`, body) : api.post('/api/setup/equipment', body);
    },
    onSuccess: (_r, e) => {
      setEditing(null); setError(null); setConflict(null);
      onSaved(`${e.name.trim()} saved.`);
      void queryClient.invalidateQueries({ queryKey: ['reference'] });
    },
    onError: (err: Error, e) => (err instanceof ConflictError && e.equipmentTypeId > 0 ? setConflict(err.message) : setError(err.message)),
  });

  return (
    <>
      <div className="page-header" style={{ marginBottom: 10 }}>
        <div className="section-label" style={{ marginBottom: 2 }}>SUPPLIES</div>
        {canEdit && !editing && (
          <button className="btn btn--sm" onClick={() => setEditing({ equipmentTypeId: 0, name: '', isActive: true, rowVersionNo: 0 })}>
            Add supply
          </button>
        )}
      </div>
      {conflict && <ConflictBanner message={conflict} onReload={() => { setConflict(null); setEditing(null); void queryClient.invalidateQueries({ queryKey: ['reference'] }); }} />}
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {editing && (
        <div className="card" style={{ marginBottom: 12, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ minWidth: 260, flex: 1 }}>
            <label className="field-label" htmlFor="eq-name">NAME</label>
            <input id="eq-name" className="control" style={{ width: '100%' }} value={editing.name} placeholder="31 gallon bags"
              onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, paddingBottom: 8 }}>
            <input type="checkbox" checked={editing.isActive} onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })} />
            Active
          </label>
          <button className="btn btn--primary" disabled={!editing.name.trim() || save.isPending} onClick={() => save.mutate(editing)}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          <button className="btn" onClick={() => { setEditing(null); setError(null); }}>Cancel</button>
        </div>
      )}

      <div className="table-wrap" style={{ marginBottom: 26 }}>
        <table className="table">
          <thead>
            <tr>
              <SortableHeader label="NAME" column="name" sort={sort} onSort={onSort} />
              <SortableHeader label="LOCATIONS" column="locations" sort={sort} onSort={onSort} style={{ textAlign: 'center' }}
                title="Active locations with this supply on their count list" />
              <SortableHeader label="STATUS" column="status" sort={sort} onSort={onSort} />
              {canEdit && <th />}
            </tr>
          </thead>
          <tbody>
            {reference.equipment.length === 0 && <EmptyRow colSpan={canEdit ? 4 : 3}>No supplies yet.</EmptyRow>}
            {rows.map((e) => (
              <tr key={e.equipmentTypeId} style={e.isActive ? undefined : { color: 'var(--muted)' }}>
                <td className="strong">{e.name}</td>
                <td style={{ textAlign: 'center' }}>
                  {(() => {
                    const n = countingIt(e);
                    return n === 0 ? '—' : (
                      <Link to={`/setup/locations?supply=${e.equipmentTypeId}`} title={`See the ${n} locations that count ${e.name}`}
                        style={{ fontWeight: 600, textDecoration: 'none' }}>
                        {n}
                      </Link>
                    );
                  })()}
                </td>
                <td>{e.isActive ? 'Active' : 'Inactive — no longer asked'}</td>
                {canEdit && <td className="num"><button className="btn btn--sm" onClick={() => setEditing(e)}>Edit</button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function CountLists({ canEdit, onSaved }: { canEdit: boolean; onSaved: (message: string) => void }) {
  const { reference } = useApp();
  return (
    <>
      <div className="section-label" style={{ marginBottom: 2 }}>COUNT LISTS AND CADENCE</div>
      <div className="meta" style={{ marginBottom: 12, maxWidth: 760 }}>
        A location is asked only for the items on its type's list, and must answer every one — 0 when none are on hand —
        so a truck is not shown twelve boxes for three answers, and "none on hand" never looks like "not counted".
        Weekly counts end on Wednesday. Grace days are how long after that a count still counts as on time.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))', gap: 14 }}>
        {reference.types.map((t) => (
          <TypeCard key={`${t.locationTypeId}-${t.rowVersionNo}`} type={t} canEdit={canEdit} onSaved={onSaved} />
        ))}
      </div>
    </>
  );
}

function TypeCard({ type, canEdit, onSaved }: { type: LocationType; canEdit: boolean; onSaved: (message: string) => void }) {
  const { reference } = useApp();
  const queryClient = useQueryClient();
  const [frequency, setFrequency] = useState<Frequency>(type.frequency);
  const [grace, setGrace] = useState(String(type.graceDays));
  const [selected, setSelected] = useState<Set<number>>(new Set(type.equipmentTypeIds));
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);

  const locations = reference.locations.filter((l) => l.locationTypeId === type.locationTypeId && l.isActive).length;
  // Inactive items stay visible while they are still on the list, so they can be taken off.
  const choices = reference.equipment.filter((e) => e.isActive || selected.has(e.equipmentTypeId));
  const dirty = frequency !== type.frequency || grace !== String(type.graceDays)
    || selected.size !== type.equipmentTypeIds.length || type.equipmentTypeIds.some((id) => !selected.has(id));

  const save = useMutation({
    mutationFn: () =>
      api.put(`/api/setup/location-types/${type.locationTypeId}`, {
        frequency, graceDays: Number(grace || 0), equipmentTypeIds: [...selected], rowVersionNo: type.rowVersionNo,
      }),
    onSuccess: () => {
      onSaved(`${type.name}: count list and cadence saved.`);
      void queryClient.invalidateQueries({ queryKey: ['reference'] });
    },
    onError: (e: Error) => (e instanceof ConflictError ? setConflict(e.message) : setError(e.message)),
  });

  const toggle = (id: number) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <div style={{ fontSize: 14.5, fontWeight: 700 }}>{type.name}</div>
        <div className="meta">{locations} active {locations === 1 ? 'location' : 'locations'}</div>
      </div>
      {conflict && <ConflictBanner message={conflict} onReload={() => { setConflict(null); void queryClient.invalidateQueries({ queryKey: ['reference'] }); }} />}
      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      <div style={{ display: 'flex', gap: 12, margin: '10px 0' }}>
        <div>
          <label className="field-label" htmlFor={`freq-${type.locationTypeId}`}>COUNTED</label>
          <select id={`freq-${type.locationTypeId}`} className="control" value={frequency} disabled={!canEdit}
            onChange={(e) => setFrequency(e.target.value as Frequency)}>
            <option value="weekly">Weekly, ending Wednesday</option>
            <option value="daily">Daily</option>
          </select>
        </div>
        <div>
          <label className="field-label" htmlFor={`grace-${type.locationTypeId}`}>GRACE DAYS</label>
          <input id={`grace-${type.locationTypeId}`} className="control" style={{ width: 70 }} inputMode="numeric"
            value={grace} disabled={!canEdit} onChange={(e) => setGrace(e.target.value.replace(/[^\d]/g, '').slice(0, 2))} />
        </div>
      </div>

      <div className="field-label">COUNT LIST · {selected.size} {selected.size === 1 ? 'ITEM' : 'ITEMS'}</div>
      {selected.size === 0 && (
        <div className="meta" style={{ color: 'var(--orange-text)', marginBottom: 6 }}>
          Nothing on the list: locations of this type cannot enter a count.
        </div>
      )}
      <div style={{ display: 'grid', gap: 3 }}>
        {choices.map((e) => (
          <label key={e.equipmentTypeId} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
            <input type="checkbox" checked={selected.has(e.equipmentTypeId)} disabled={!canEdit} onChange={() => toggle(e.equipmentTypeId)} />
            {e.name}{e.isActive ? '' : ' (inactive)'}
          </label>
        ))}
      </div>

      {canEdit && (
        <button className="btn btn--primary btn--sm" style={{ marginTop: 12 }} disabled={!dirty || save.isPending}
          onClick={() => { setError(null); save.mutate(); }}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      )}
    </div>
  );
}
